import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Typography } from "@mui/joy";
import { UploadFileOutlined } from "@mui/icons-material";
import { connect, useDispatch } from "react-redux";
import {
  uploadPartnerPhotoRequest,
  uploadPartnerPhotoReset,
  viewPartnerRequest,
} from "../../../store/actions";
import { STATUS } from "../../../constant";
import { toast } from "react-toastify";

const MAX_SIZE_MB = 2;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
const REQUIRED_WIDTH = 50;
const REQUIRED_HEIGHT = 50;

const PhotoUploadForm = ({
  id,
  setUploadModal,
  loginResult,
  uploadPartnerPhotoStatus,
  uploadPartnerPhotoResult,
  uploadPartnerPhotoErrorMessage,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);

  const isLoading = uploadPartnerPhotoStatus === STATUS.LOADING;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ✅ Check file size
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`Image must be smaller than ${MAX_SIZE_MB}MB`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    // ✅ Check dimensions and aspect ratio
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;

      if (width !== height) {
        toast.error("Image must have a 1:1 aspect ratio (square)");
        URL.revokeObjectURL(objectUrl);
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      if (width < REQUIRED_WIDTH || height < REQUIRED_HEIGHT) {
        toast.error(
          `Image must be at least ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT}px`,
        );
        URL.revokeObjectURL(objectUrl);
        if (inputRef.current) inputRef.current.value = "";
        return;
      }

      // ✅ All checks passed
      setPhoto(file);
      setPreview(objectUrl);
    };

    img.onerror = () => {
      toast.error("Failed to load image. Please try another file.");
      URL.revokeObjectURL(objectUrl);
      if (inputRef.current) inputRef.current.value = "";
    };

    img.src = objectUrl;
  };

  const handleUpload = () => {
    if (!photo) return;
    const formData = new FormData();
    formData.append("photo", photo);
    formData.append("id", String(id));
    dispatch(uploadPartnerPhotoRequest(loginResult.token, formData));
  };

  const handleClear = () => {
    setPhoto(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    if (uploadPartnerPhotoStatus === STATUS.SUCCESS) {
      toast.success("Photo uploaded successfully");
      handleClear();
      dispatch(uploadPartnerPhotoReset());
      dispatch(viewPartnerRequest(loginResult.token, { id }));
      setUploadModal(false);
    } else if (uploadPartnerPhotoStatus === STATUS.ERROR) {
      toast.error(uploadPartnerPhotoErrorMessage, {
        toastId: "uploadPhotoError",
      });
      dispatch(uploadPartnerPhotoReset());
      setUploadModal(false);
    }
  }, [
    uploadPartnerPhotoStatus,
    uploadPartnerPhotoErrorMessage,
    dispatch,
    setUploadModal,
    id,
    loginResult,
  ]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <input
        ref={inputRef}
        type="file"
        name="photo"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Preview or placeholder */}
      <Box
        onClick={() => !isLoading && inputRef.current?.click()}
        sx={{
          width: "100%",
          height: 220,
          borderRadius: 8,
          border: "2px dashed",
          borderColor: preview ? "transparent" : "neutral.400",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isLoading ? "not-allowed" : "pointer",
          bgcolor: preview ? "transparent" : "neutral.50",
          transition: "border-color 0.2s",
          "&:hover": { borderColor: isLoading ? "neutral.400" : "primary.400" },
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Box sx={{ textAlign: "center", color: "neutral.500" }}>
            <UploadFileOutlined sx={{ fontSize: 40, mb: 1 }} />
            <Typography level="body-sm">Click to select a photo</Typography>
            <Typography level="body-xs" sx={{ mt: 0.5, color: "neutral.400" }}>
              Square image · Min 50×50px · Max 2MB
            </Typography>
          </Box>
        )}
      </Box>

      {/* File name */}
      {photo && (
        <Typography level="body-xs" sx={{ color: "neutral.500" }}>
          {photo.name}
        </Typography>
      )}

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          color="neutral"
          fullWidth
          disabled={isLoading}
          onClick={() => inputRef.current?.click()}
        >
          {preview ? "Change Photo" : "Select Photo"}
        </Button>

        {preview && (
          <>
            <Button
              variant="outlined"
              color="danger"
              disabled={isLoading}
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              color="primary"
              fullWidth
              loading={isLoading}
              onClick={handleUpload}
            >
              Upload
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ partner, auth }) => {
  const { otpResult: loginResult } = auth;
  const {
    uploadPartnerPhotoStatus,
    uploadPartnerPhotoResult,
    uploadPartnerPhotoErrorMessage,
  } = partner;
  return {
    loginResult,
    uploadPartnerPhotoStatus,
    uploadPartnerPhotoResult,
    uploadPartnerPhotoErrorMessage,
  };
};

export default connect(mapStateToProps, {})(PhotoUploadForm);
